import React, { useEffect, useState } from 'react';
import {
  RouteProps,
  withRouter,
  useHistory,
} from 'react-router-dom';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import zxcvbn from 'zxcvbn';
import LinearProgress from '@material-ui/core/LinearProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import { Box, CircularProgress } from '@material-ui/core';
import {
  FormContainer,
  TextFieldContainer,
  StyledTextField,
} from './FormStyles';
import { changePassword } from '../../api/api';
import { useUser } from '../../contexts/UserContext';
import ErrorMessage from '../ErrorMessage';
import { passwordStrengthColors, passwordStrengthLabels } from '../../constants/User';
import NotificationToast from '../notifications/NotificationToast';
import i18n from '../../i18n/i18n';
import { useTheme } from '../../contexts/ThemeContext';
import GalpUserMenu from '../GalpUserMenu';

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  flex:1
`;

const getPasswordStrength = (text?: string | null) => {
  const score = Math.min(zxcvbn(text).score, 3);
  return {
    score,
    message: i18n.t(passwordStrengthLabels[score]),
    color: passwordStrengthColors[score],
  };
};

const StyledButton = styled(({ $bgColor, ...rest }) => (
  <Button
    {...rest}
  />
))`
  && {
    background-color: ${({ $bgColor }) => $bgColor || 'rgb(250,194,3)'};;
    border-width: 0;
    border-radius: 10px;
    padding: 10px 20px;
    margin: 10px;
    width: 120px;
    text-transform: capitalize;
    font-size: 0.85em;

    ${({ disabled }) => disabled && `
     background-color: rgb(243, 243, 243);
     color: rgba(255,255,255,0.15)
     border-width: 0;
     border-radius: 10px;
     padding: 10px 20px;
     margin: 10px;
     width: 120px;
     text-transform: capitalize;
     font-size: 0.85em;
  `
}
`;

const StyledDiv = styled.div`
    padding-top: 20px;
    padding-bottom: 20px;
`;

const Spacer = styled.div`
  margin-bottom: 60px;
`;

const HelexiaInputWrapper = styled.div<{ $hasError?: boolean }>`
  background: #efefef;
  border-radius: 12px;
  height: 36px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  border: 2px solid ${({ $hasError }) => ($hasError ? '#d01818' : 'transparent')};
`;

const HelexiaInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  color: #3f3f3f;
  font-family: Geologica, sans-serif;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: -0.98px;
  width: 100%;
  &::placeholder {
    color: #6a6a6a;
  }
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0px 1000px #efefef inset;
    box-shadow: 0 0 0px 1000px #efefef inset;
    -webkit-text-fill-color: #3f3f3f;
  }
`;

type Error = {
  currentPasswordError: string | null,
  newPasswordError: string | null,
  confirmNewPasswordError: string | null,
  changePasswordError: string | null
}

const ChangePwForm = () => {
  const { user, authenticateNewToken } = useUser();
  const [currentPassword, setCurrentPassword] = React.useState<string | null>(null);
  const [newPassword, setNewPassword] = React.useState<string | null>(null);
  const [confirmNewPassword, setConfirmNewPassword] = React.useState<string | null>(null);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notification, setNotification] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [wrongPasswordError, setWrongPasswordError] = useState<string | null>(null);
  const [error, setError] = React.useState<Error>({
    currentPasswordError: null,
    newPasswordError: null,
    confirmNewPasswordError: null,
    changePasswordError: null,
  });
  const { theme, themeName } = useTheme();
  const isHelexia = themeName === 'helexia';
  const isGalp = themeName === 'galp';
  const history = useHistory();

  const galpInitials = (() => {
    const authUser = (user as any)?.user;
    const source = authUser?.name || (authUser?.email || '').split('@')[0];
    const parts = source.split(/[\s._-]+/).filter(Boolean);
    const raw = parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : (parts[0]?.[0] || '');
    return raw.toUpperCase();
  })();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await changePassword(user, currentPassword, newPassword);
      setError((prevState) => ({ ...prevState, changePasswordError: null }));
      await authenticateNewToken(response.token);
      setLoading(false);
      setNotification({
        severity: 'success',
        title: i18n.t('success'),
        message: i18n.t('password_changed_successfully'),
      });
      setNewPassword(null);
      setConfirmNewPassword(null);
      setShowNotification(true);
    } catch (ex) {
      setLoading(false);
      if (isHelexia) {
        setWrongPasswordError(i18n.t('wrong_password'));
        return;
      }
      setNotification({
        severity: 'error',
        title: i18n.t('error'),
        message: i18n.t('error_occured_try_later'),
      });
      setShowNotification(true);
    }
  };

  useEffect(() => {
    const newErrors = { ...error };

    if (!currentPassword || currentPassword === '') {
      newErrors.currentPasswordError = i18n.t('mandatory');
    } else {
      newErrors.currentPasswordError = null;
    }

    if (getPasswordStrength(newPassword || '').score < 3) {
      newErrors.newPasswordError = i18n.t('password_too_simple');
    } else {
      newErrors.newPasswordError = null;
    }

    if (!newPassword || newPassword === '') {
      newErrors.newPasswordError = i18n.t('mandatory');
    } else if (newErrors.newPasswordError === null) {
      newErrors.newPasswordError = null;
    }

    if (!confirmNewPassword || confirmNewPassword === '') {
      newErrors.confirmNewPasswordError = i18n.t('mandatory');
    } else {
      newErrors.confirmNewPasswordError = null;
    }

    if (confirmNewPassword && newPassword !== confirmNewPassword) {
      newErrors.confirmNewPasswordError = i18n.t('new_different_password');
    } else if (newErrors.confirmNewPasswordError !== i18n.t('mandatory')) newErrors.confirmNewPasswordError = null;

    setError(newErrors);
  }, [currentPassword, newPassword, confirmNewPassword]);

  let passwordStrength;
  if (newPassword) {
    passwordStrength = getPasswordStrength(newPassword);
  }

  if (isGalp) {
    const galpFieldGroup: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
      width: '100%',
    };
    const galpLabel: React.CSSProperties = {
      fontSize: '14px',
      lineHeight: '22px',
      color: '#111110',
      margin: 0,
    };
    const galpInputBox: React.CSSProperties = {
      border: '2px solid #bab9ba',
      borderRadius: '14px',
      height: '46px',
      display: 'flex',
      alignItems: 'center',
      padding: '0 10px',
      boxSizing: 'border-box',
      width: '100%',
    };
    const galpInput: React.CSSProperties = {
      border: 'none',
      outline: 'none',
      background: 'transparent',
      width: '100%',
      fontFamily: theme.bodyFont,
      fontSize: '16px',
      color: '#0b0b0b',
    };
    const galpErrorStyle: React.CSSProperties = { color: '#d01818', fontSize: '12px', margin: '4px 0 0 0' };
    const mandatory = i18n.t('mandatory');
    const submitDisabled = !!(
      error.currentPasswordError
      || error.newPasswordError
      || error.confirmNewPasswordError
      || error.changePasswordError
    );

    return (
      <div style={{
        width: '100%',
        height: '100dvh',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        fontFamily: theme.bodyFont,
      }}
      >
        <NotificationToast
          open={showNotification}
          message={notification && notification.message}
          title={notification && notification.title}
          severity={notification && notification.severity}
          onClose={() => setShowNotification(false)}
        />

        {/* Header */}
        <div style={{
          position: 'relative',
          background: '#f7f4ed',
          minHeight: '94px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 16px',
          borderBottomLeftRadius: '14px',
          borderBottomRightRadius: '14px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
        >
          <button
            type="button"
            aria-label="back"
            onClick={() => history.goBack()}
            style={{
              position: 'absolute',
              left: '16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6 1L1 6L6 11"
                stroke="#0b0b0b"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <p style={{
            fontFamily: theme.titleFont,
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '24px',
            color: '#0b0b0b',
            margin: 0,
          }}
          >
            {i18n.t('change_password')}
          </p>
          <GalpUserMenu initials={galpInitials} />
        </div>

        {/* Body */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" style={{ flex: 1 }}>
            <CircularProgress style={{ color: theme.loadingIconColor }} />
          </Box>
        ) : (
          <form
            id="changepw"
            noValidate
            autoComplete="off"
            onSubmit={onSubmit}
            style={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              padding: '24px 16px',
              boxSizing: 'border-box',
            }}
          >
            <div style={galpFieldGroup}>
              <label htmlFor="galp-current-pw" style={galpLabel}>{i18n.t('current_password')}</label>
              <div style={galpInputBox}>
                <input
                  id="galp-current-pw"
                  type="password"
                  value={currentPassword || ''}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  style={galpInput}
                />
              </div>
            </div>

            <div style={galpFieldGroup}>
              <label htmlFor="galp-new-pw" style={galpLabel}>{i18n.t('new_password')}</label>
              <div style={galpInputBox}>
                <input
                  id="galp-new-pw"
                  type="password"
                  value={newPassword || ''}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={galpInput}
                />
              </div>
              {error.newPasswordError && error.newPasswordError !== mandatory && (
                <p style={galpErrorStyle}>{error.newPasswordError}</p>
              )}
            </div>

            <div style={galpFieldGroup}>
              <label htmlFor="galp-confirm-pw" style={galpLabel}>{i18n.t('confirm_password')}</label>
              <div style={galpInputBox}>
                <input
                  id="galp-confirm-pw"
                  type="password"
                  value={confirmNewPassword || ''}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  style={galpInput}
                />
              </div>
              {error.confirmNewPasswordError && error.confirmNewPasswordError !== mandatory && (
                <p style={galpErrorStyle}>{error.confirmNewPasswordError}</p>
              )}
            </div>
          </form>
        )}

        {/* Footer */}
        <div style={{
          borderTop: '1px solid #bababa',
          background: 'white',
          height: '85px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 32px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
        >
          <button
            type="submit"
            form="changepw"
            disabled={submitDisabled}
            style={{
              height: '36px',
              width: '100%',
              borderRadius: '57px',
              background: submitDisabled ? '#df9680' : theme.buttonColor,
              border: 'none',
              color: theme.buttonTextColor || 'white',
              fontFamily: theme.bodyFont,
              fontSize: '14px',
              cursor: submitDisabled ? 'not-allowed' : 'pointer',
              boxShadow: '0px 2px 0px rgba(0,0,0,0.04)',
            }}
          >
            {i18n.t('save')}
          </button>
        </div>
      </div>
    );
  }

  if (isHelexia) {
    const helexiaErrorStyle: React.CSSProperties = {
      color: '#d01818',
      fontFamily: 'Geologica, sans-serif',
      fontWeight: 500,
      fontSize: '12px',
      margin: '4px 0 0 0',
    };

    const isDisabled = !!(
      error.currentPasswordError
      || error.newPasswordError
      || error.confirmNewPasswordError
      || error.changePasswordError
    );

    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress style={{ color: theme.loadingIconColor }} />
        </Box>
      );
    }

    return (
      <>
        <NotificationToast
          open={showNotification}
          message={notification && notification.message}
          title={notification && notification.title}
          severity={notification && notification.severity}
          onClose={() => setShowNotification(false)}
        />
        <div style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '100%', flex: 1,
        }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '40px', width: '100%' }}>
            <p style={{
              fontFamily: 'Geologica, sans-serif',
              fontWeight: 600,
              fontSize: '32px',
              lineHeight: '32px',
              color: '#004e74',
              textAlign: 'center',
              margin: 0,
              width: '100%',
              whiteSpace: 'pre-line',
            }}
            >
              {i18n.t('change_password_title')}
            </p>
            <form
              id="helexia-changepw"
              onSubmit={onSubmit}
              style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%' }}
            >
              <div>
                <HelexiaInputWrapper $hasError={!!wrongPasswordError}>
                  <HelexiaInput
                    type="password"
                    placeholder={`${i18n.t('current_password') as string} *`}
                    value={currentPassword || ''}
                    onChange={(e) => {
                      setCurrentPassword(e.target.value);
                      setWrongPasswordError(null);
                    }}
                  />
                </HelexiaInputWrapper>
                {wrongPasswordError && <p style={helexiaErrorStyle}>{wrongPasswordError}</p>}
              </div>
              <div>
                <HelexiaInputWrapper $hasError={!!(error.newPasswordError && error.newPasswordError !== i18n.t('mandatory'))}>
                  <HelexiaInput
                    type="password"
                    placeholder={`${i18n.t('new_password') as string} *`}
                    value={newPassword || ''}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </HelexiaInputWrapper>
                {error.newPasswordError && error.newPasswordError !== i18n.t('mandatory') && (
                  <p style={helexiaErrorStyle}>{error.newPasswordError}</p>
                )}
              </div>
              <div>
                <HelexiaInputWrapper $hasError={!!(confirmNewPassword && error.confirmNewPasswordError && error.confirmNewPasswordError !== i18n.t('mandatory'))}>
                  <HelexiaInput
                    type="password"
                    placeholder={`${i18n.t('confirm_password') as string} *`}
                    value={confirmNewPassword || ''}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                  />
                </HelexiaInputWrapper>
                {confirmNewPassword && error.confirmNewPasswordError && error.confirmNewPasswordError !== i18n.t('mandatory') && (
                  <p style={helexiaErrorStyle}>{i18n.t('passwords_do_not_coincide')}</p>
                )}
              </div>
            </form>
          </div>
          <button
            type="submit"
            form="helexia-changepw"
            disabled={isDisabled}
            style={{
              background: isDisabled
                ? '#d0d0d0'
                : 'linear-gradient(90deg, #fbd802 0%, #f7c749 100%)',
              borderRadius: '12px',
              height: '36px',
              border: 'none',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              color: 'white',
              fontFamily: 'Geologica, sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              letterSpacing: '-0.42px',
              width: '100%',
              flexShrink: 0,
            }}
          >
            {i18n.t('alter')}
          </button>
        </div>
      </>
    );
  }

  return loading ? (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      height="50%"
    >
      <CircularProgress color="inherit" style={{ color: theme.loadingIconColor }} />
    </Box>
  ) : (
    <FormContainer
      style={{
        margin: '0 2dvh 2dvh 2dvh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'start',
        height: '100%',
      }}
    >
      <NotificationToast
        open={showNotification}
        message={notification && notification.message}
        title={notification && notification.title}
        severity={notification && notification.severity}
        onClose={() => setShowNotification(false)}
      />
      <form style={{ width: '100%' }} noValidate autoComplete="off" onSubmit={onSubmit} id="changepw">
        <StyledDiv>
          <span>{i18n.t('current_password')}</span>
          <StyledTextField
            fullWidth
            type="password"
            onChange={(e) => setCurrentPassword(e.target.value)}
            error={!!error.currentPasswordError}
            id="currentPassword"
            helperText={error.currentPasswordError ? error.currentPasswordError : ''}
          />
        </StyledDiv>
        <StyledDiv>
          <span>{i18n.t('new_password')}</span>
          <StyledTextField
            fullWidth
            type="password"
            onChange={(e) => setNewPassword(e.target.value)}
            error={!!error.newPasswordError}
            id="newPassword"
            helperText={(
              <>
                {(
                  newPassword && (
                    <LinearProgress
                      style={{ backgroundColor: passwordStrength.color }}
                      value={100 - (passwordStrength.score + 1) * 25}
                      variant="determinate"
                    />
                  )
                )}
                {error.newPasswordError ? error.newPasswordError : ' '}
              </>
            )}
            InputProps={{
              endAdornment: newPassword ? (
                <InputAdornment position="end">
                  <span style={{ color: passwordStrength.color }}>
                    {passwordStrength.message}
                  </span>
                </InputAdornment>
              ) : null,
            }}
          />
        </StyledDiv>
        <StyledDiv>
          <span>{i18n.t('confirm_password')}</span>
          <StyledTextField
            fullWidth
            type="password"
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            error={!!error.confirmNewPasswordError}
            id="confirmNewPassword"
            helperText={error.confirmNewPasswordError ? error.confirmNewPasswordError : ''}
          />
        </StyledDiv>
        <ButtonsContainer>
          <StyledButton
            variant="outlined"
            color="secondary"
            $bgColor={theme.buttonColor}
            type="submit"
            form="changepw"
            disabled={
                error.currentPasswordError
                  || error.newPasswordError
                  || error.confirmNewPasswordError
                  || error.changePasswordError
            }
          >
            {i18n.t('save')}
          </StyledButton>
        </ButtonsContainer>
        {error.changePasswordError && (<ErrorMessage error={error.changePasswordError} />)}
      </form>
      <Spacer />
    </FormContainer>
  );
};

export default withRouter(ChangePwForm);
